import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Spinner, Dropdown } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const UserPermission = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('Administrator');
  const [menus, setMenus] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [activeMenu, setActiveMenu] = useState('');
  const [time, setTime] = useState({ date: '', clock: '' });

  // ==========================================
  // STATE PERMISSION & GROUP DATA
  // ==========================================
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // ==========================================
  // STATE PENCARIAN GROUP USER FILTER
  // ==========================================
  const [groupFilterQuery, setGroupFilterQuery]       = useState('');
  const [showGroupFilterDrop, setShowGroupFilterDrop] = useState(false);
  const groupFilterRef = useRef(null);

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);

    const savedMenu = localStorage.getItem('mainmenu');
    if (savedMenu) {
      const parsedMenu = JSON.parse(savedMenu);
      setMenus(parsedMenu);
      if (parsedMenu.length > 0) setActiveMenu(parsedMenu[0].id_mn);
    }

    const updateTime = () => {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: now.toLocaleDateString('id-ID', options),
        clock: now.toLocaleTimeString('id-ID') + ' WIB'
      });
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchGroups();
  }, []);

  // Tutup dropdown group filter saat klik di luar
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (groupFilterRef.current && !groupFilterRef.current.contains(e.target)) {
        setShowGroupFilterDrop(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // ==========================================
  // FETCH DATA GROUPS
  // ==========================================
  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/show_group?token=${encodeURIComponent(token)}`);
      const json = await response.json();
      setGroups(json.list_group || json.data || []);
    } catch (err) {
      console.error('Gagal mengambil data group:', err);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // ==========================================
  // FETCH DATA PERMISSIONS (DENGAN PARAMETER GROUP)
  // ==========================================
  const fetchPermissions = async (groupId = selectedGroup) => {
    if (!groupId) {
      setPermissions([]);
      setFetchError('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setFetchError('');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      
      const paramStr = `&group_id=${encodeURIComponent(groupId)}&group=${encodeURIComponent(groupId)}`;
      const response = await fetch(`${apiUrl}/show_permision?token=${encodeURIComponent(token)}${paramStr}`);
      
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const json = await response.json();
      setPermissions(json.data || []);
    } catch (err) {
      setFetchError('Gagal mengambil data permission: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler pencarian Group User
  const handleGroupFilterInput = (e) => {
    const val = e.target.value;
    setGroupFilterQuery(val);
    if (!val.trim()) {
      setSelectedGroup('');
      fetchPermissions('');
    }
    setShowGroupFilterDrop(true);
  };

  const handleSelectFilterGroup = (item) => {
    if (item === null) {
      setSelectedGroup('');
      setGroupFilterQuery('');
      fetchPermissions('');
    } else {
      setSelectedGroup(item.kode);
      setGroupFilterQuery(`${item.kode} - ${item.nama}`);
      fetchPermissions(item.kode);
    }
    setShowGroupFilterDrop(false);
  };

  const getMenuIcon = (menuName) => {
    switch (menuName.toUpperCase()) {
      case 'MASTER':        return 'bi-folder2-open';
      case 'GUDANG':        return 'bi-box-seam';
      case 'ADM GUDANG':    return 'bi-building-gear';
      case 'APOTEKER':      return 'bi-prescription2';
      case 'ADMINISTRATOR': return 'bi-person-workspace';
      case 'SETTING':       return 'bi-sliders';
      default:              return 'bi-grid-1x2';
    }
  };

  const getCategoryName = (id_mn) => {
    const found = menus.find(m => m.id_mn === id_mn);
    if (found) return found.des_mn;
    switch (id_mn) {
      case '01': return 'MASTER';
      case '02': return 'GUDANG';
      case '03': return 'ADM GUDANG';
      case '04': return 'APOTEKER';
      case '05': return 'ADMINISTRATOR';
      case '99': return 'SETTING';
      default:   return `KATEGORI ${id_mn}`;
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Filter permission data berdasarkan pencarian
  const filteredPermissions = permissions.filter((p) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (p.id_mm && p.id_mm.toLowerCase().includes(q)) ||
      (p.id_mn && p.id_mn.toLowerCase().includes(q)) ||
      (p.nama && p.nama.toLowerCase().includes(q)) ||
      (p.nm_mm && p.nm_mm.toLowerCase().includes(q)) ||
      (p.info && p.info.toLowerCase().includes(q)) ||
      (p.lnk && p.lnk.toLowerCase().includes(q))
    );
  });

  // SORT BY ID MAIN (id_mn) ASCENDING, THEN BY ID SUB (id_mm) ASCENDING
  const sortedPermissions = [...filteredPermissions].sort((a, b) => {
    const mnA = (a.id_mn || '').toString();
    const mnB = (b.id_mn || '').toString();
    if (mnA !== mnB) {
      return mnA.localeCompare(mnB, undefined, { numeric: true });
    }
    const mmA = (a.id_mm || '').toString();
    const mmB = (b.id_mm || '').toString();
    return mmA.localeCompare(mmB, undefined, { numeric: true });
  });

  return (
    <div style={styles.appContainer}>

      {/* ══════════════ NAVBAR ══════════════ */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home">
            <i className="bi bi-layers-half me-2 text-warning"></i>
            <span className="fw-bold text-white">Solusi Integrasi Layanan Kesehatan ( SILK )</span>
          </a>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 flex-row gap-2">
              {menus.map((item) => {
                const isActive = location.pathname === item.lnk;
                return (
                  <li className="nav-item" key={item.id_mn}>
                    <a
                      className={`nav-link-custom ${isActive ? 'active' : ''}`}
                      href={`#${item.des_mn.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setActiveMenu(item.id_mn)}
                      style={{ ...styles.navLinkCustom, ...(isActive ? styles.navLinkActive : {}) }}
                    >
                      <i className={`bi ${getMenuIcon(item.des_mn)}`}></i>
                      {item.des_mn.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center text-white text-end gap-3 ms-auto">
              <div className="small border-end pe-3 border-light border-opacity-25 d-none d-sm-block">
                <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>{time.date}</div>
                <div className="text-warning" style={{ fontSize: '0.7rem' }}>{time.clock}</div>
              </div>

              <Dropdown align="end">
                <Dropdown.Toggle
                  as="div" role="button"
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded-3 custom-profile-toggle"
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center bg-warning text-dark fw-bold rounded-circle shadow-sm"
                    style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}
                  >
                    {username ? username.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div className="d-none d-md-block text-start">
                    <div className="text-white fw-semibold lh-1" style={{ fontSize: '0.85rem' }}>{username}</div>
                    <div className="text-white-50 mt-1" style={{ fontSize: '0.7rem' }}>Online</div>
                  </div>
                  <i className="bi bi-chevron-down text-white-50 ms-1 small"></i>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow border-0 mt-2 py-2 style-dropdown-menu" style={{ minWidth: '180px', borderRadius: '10px' }}>
                  <Dropdown.Item onClick={handleLogout} className="py-2 text-danger small d-flex align-items-center gap-2 fw-semibold">
                    <i className="bi bi-box-arrow-right"></i> Keluar Aplikasi
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Container>
      </nav>

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <div style={styles.mainContent}>
        <Row className="g-3">
          <Col lg={12}>
            <Card style={styles.mainCard}>

              {/* Card Header */}
              <Card.Header style={{ ...styles.cardHeader, ...styles.bgMenuHeader }} className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-shield-lock-fill text-warning"></i>
                  <span>SETTING PERMISSION USER</span>
                  {location.state?.info && (
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.75)',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      padding: '1px 10px',
                    }}>
                      <i className="bi bi-info-circle me-1"></i>{location.state.info}
                    </span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  {selectedGroup && (
                    <span className="badge rounded-pill bg-warning text-dark fw-bold px-2.5 py-1" style={{ fontSize: '0.72rem' }}>
                      Group: {groups.find(g => g.kode === selectedGroup)?.nama || selectedGroup}
                    </span>
                  )}
                  <span className="badge rounded-pill fw-normal px-2" style={{ background: 'rgba(255,255,255,0.15)', fontSize: '0.7rem' }}>
                    {sortedPermissions.length} Permisi
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm d-inline-flex align-items-center gap-1 fw-semibold shadow-sm ms-2"
                    onClick={() => navigate('/home')}
                    style={styles.actionBtn}
                  >
                    <i className="bi bi-house-door"></i><span>Home</span>
                  </button>
                </div>
              </Card.Header>

              <Card.Body style={{ padding: '1rem' }}>
                {/* Toolbar */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Search Input Filter Permisi */}
                    <InputGroup size="sm" style={{ maxWidth: '280px' }}>
                      <InputGroup.Text style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b' }}>
                        <i className="bi bi-search" style={{ fontSize: '0.75rem' }}></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Cari ID menu, nama, info, link..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ fontSize: '0.8rem', border: '1px solid #e2e8f0', borderLeft: 'none' }}
                      />
                      {searchQuery && (
                        <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery('')} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      )}
                    </InputGroup>

                    {/* Searchable Autocomplete Dropdown Group User Filter */}
                    <div className="position-relative" ref={groupFilterRef} style={{ minWidth: '250px', maxWidth: '300px' }}>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text" style={{ background: '#f0fdf4', border: '1px solid #0d9488', borderRight: 'none', color: '#0d9488' }}>
                          <i className="bi bi-people-fill" style={{ fontSize: '0.75rem' }}></i>
                        </span>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ketik kode / nama group user..."
                          value={groupFilterQuery}
                          onChange={handleGroupFilterInput}
                          onFocus={() => setShowGroupFilterDrop(true)}
                          autoComplete="off"
                          style={{
                            fontSize: '0.78rem',
                            border: '1px solid #0d9488',
                            borderLeft: 'none',
                            fontWeight: selectedGroup ? '600' : 'normal',
                            color: selectedGroup ? '#0f766e' : '#334155'
                          }}
                        />
                        {isLoadingGroups ? (
                          <span className="input-group-text" style={{ background: '#fff', border: '1px solid #0d9488', borderLeft: 'none' }}>
                            <Spinner animation="border" size="sm" style={{ width: '0.75rem', height: '0.75rem', color: '#0d9488' }} />
                          </span>
                        ) : groupFilterQuery ? (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleSelectFilterGroup(null)}
                            style={{ border: '1px solid #0d9488', borderLeft: 'none', background: '#fff', fontSize: '0.7rem' }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        ) : null}
                      </div>

                      {/* Dropdown Hasil Pencarian Group */}
                      {showGroupFilterDrop && (
                        <div
                          className="position-absolute w-100 rounded-2 shadow-lg"
                          style={{
                            top: '100%', left: 0, zIndex: 9999,
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            maxHeight: '220px', overflowY: 'auto',
                            marginTop: '2px'
                          }}
                        >
                          <div
                            onClick={() => handleSelectFilterGroup(null)}
                            className="px-3 py-1.5 text-muted fw-semibold"
                            style={{ cursor: 'pointer', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}
                          >
                            <i className="bi bi-grid-fill me-1 text-teal"></i> -- Semua Group User --
                          </div>

                          {groups
                            .filter(g =>
                              !groupFilterQuery ||
                              g.kode.toLowerCase().includes(groupFilterQuery.toLowerCase()) ||
                              g.nama.toLowerCase().includes(groupFilterQuery.toLowerCase())
                            )
                            .map((item) => (
                              <div
                                key={item.kode}
                                onClick={() => handleSelectFilterGroup(item)}
                                className="d-flex align-items-center gap-2 px-3 py-2"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '0.78rem',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.1s',
                                  background: selectedGroup === item.kode ? '#f0fdf4' : '#fff'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                onMouseLeave={e => e.currentTarget.style.background = selectedGroup === item.kode ? '#f0fdf4' : '#fff'}
                              >
                                <span className="badge fw-normal flex-shrink-0" style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.6rem', minWidth: '3.5rem' }}>
                                  {item.kode}
                                </span>
                                <span className="text-dark fw-medium">{item.nama}</span>
                              </div>
                            ))}

                          {groups.filter(g =>
                            !groupFilterQuery ||
                            g.kode.toLowerCase().includes(groupFilterQuery.toLowerCase()) ||
                            g.nama.toLowerCase().includes(groupFilterQuery.toLowerCase())
                          ).length === 0 && !isLoadingGroups && (
                            <div className="text-center py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                              <i className="bi bi-search me-1"></i>Tidak ada group ditemukan
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => fetchPermissions(selectedGroup)} disabled={isLoading}
                      style={{ fontSize: '0.75rem' }}>
                      <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Error Banner */}
                {fetchError && (
                  <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>{fetchError}</span>
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => fetchPermissions(selectedGroup)} style={{ fontSize: '0.7rem' }}>
                      Coba Lagi
                    </button>
                  </div>
                )}

                {/* Table (Sorted by ID Main ascending & Grouped) */}
                <div className="table-responsive rounded-3 border shadow-sm" style={{ maxHeight: '62vh' }}>
                  <Table hover size="sm" className="align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                    <thead className="sticky-top" style={{ zIndex: 10 }}>
                      <tr style={{
                        background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                        color: '#0f172a',
                        fontSize: '0.78rem',
                        fontWeight: '700',
                        letterSpacing: '0.4px',
                        borderBottom: '3px solid #0d9488',
                        textTransform: 'uppercase'
                      }}>
                        <th className="px-3 py-3 text-center" style={{ color: '#475569', width: '55px', borderRight: '1px solid #cbd5e1' }}>
                          No
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#0369a1', width: '135px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-folder-fill me-1 text-primary"></i>
                          ID Main (MN)
                          <i className="bi bi-sort-numeric-down ms-1 text-teal fs-6"></i>
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#0284c7', width: '120px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-tag-fill me-1 text-info"></i>
                          ID Sub (MM)
                        </th>
                        <th className="px-3 py-3" style={{ color: '#0f172a', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-card-checklist me-1 text-teal"></i>
                          Nama Sub Menu
                        </th>
                        <th className="px-3 py-3" style={{ color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-info-circle-fill me-1 text-secondary"></i>
                          Keterangan / Info Fitur
                        </th>
                        <th className="px-3 py-3" style={{ color: '#334155', width: '170px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-link-45deg me-1 text-success"></i>
                          Tautan (Route Link)
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#0f172a', width: '95px' }}>
                          <i className="bi bi-check-circle-fill me-1 text-success"></i>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5 text-muted">
                            <Spinner animation="border" size="sm" className="me-2" style={{ color: '#0d9488' }} />
                            Memuat detail permisi user...
                          </td>
                        </tr>
                      ) : !selectedGroup ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5 text-muted">
                            <i className="bi bi-people-fill fs-2 d-block mb-2 text-teal opacity-75"></i>
                            <div className="fw-bold text-dark" style={{ fontSize: '0.88rem' }}>Pilih Group User</div>
                            <span style={{ fontSize: '0.78rem' }}>Silakan pilih Group User pada kolom pencarian di atas untuk menampilkan detail permisi.</span>
                          </td>
                        </tr>
                      ) : sortedPermissions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-3 d-block mb-1 text-secondary opacity-50"></i>
                            Tidak ada data permisi yang ditemukan untuk group ini.
                          </td>
                        </tr>
                      ) : (
                        sortedPermissions.map((p, idx) => {
                          const isFirstOfGroup = idx === 0 || sortedPermissions[idx - 1].id_mn !== p.id_mn;

                          return (
                            <React.Fragment key={p.id_mm || idx}>
                              {/* Separator Header Kategori saat ID Main berganti */}
                              {isFirstOfGroup && (
                                <tr style={{ background: 'linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 100%)', borderTop: '2px solid #0d9488', borderBottom: '1px solid #cbd5e1' }}>
                                  <td colSpan="7" className="px-3 py-2 fw-bold text-dark" style={{ fontSize: '0.78rem', letterSpacing: '0.4px' }}>
                                    <i className="bi bi-folder2-open-fill text-teal fs-6 me-2"></i>
                                    KATEGORI UTAMA: <span className="badge bg-dark text-warning me-2 px-2 py-1" style={{ fontSize: '0.72rem' }}>MN-{p.id_mn}</span>
                                    <span className="text-teal text-uppercase fw-bold">{getCategoryName(p.id_mn)}</span>
                                  </td>
                                </tr>
                              )}

                              <tr style={{ transition: 'background 0.15s', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td className="px-3 py-2 text-center text-muted fw-medium">{idx + 1}</td>
                                
                                {/* ID Main (MN) Badge */}
                                <td className="px-3 py-2 text-center">
                                  <Badge style={{ background: '#0f172a', color: '#38bdf8', fontSize: '0.68rem', border: '1px solid #334155', fontWeight: 700, padding: '3px 8px' }}>
                                    MN-{p.id_mn}
                                  </Badge>
                                </td>

                                {/* ID Sub (MM) Badge */}
                                <td className="px-3 py-2 text-center">
                                  <Badge style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.68rem', border: '1px solid #bae6fd', fontWeight: 600, padding: '3px 8px' }}>
                                    {p.id_mm}
                                  </Badge>
                                </td>

                                {/* Nama Sub Menu */}
                                <td className="px-3 py-2 fw-semibold text-dark">
                                  <i className={`bi ${p.icon || 'bi-bookmark-check'} text-teal me-2 fs-6`}></i>
                                  {p.nama || p.nm_mm}
                                </td>

                                {/* Info */}
                                <td className="px-3 py-2 text-secondary">
                                  {p.info ? p.info : <span className="text-muted fst-italic">—</span>}
                                </td>

                                {/* Route Link */}
                                <td className="px-3 py-2">
                                  {p.lnk ? (
                                    <Badge style={{ background: '#f0fdf4', color: '#15803d', fontSize: '0.65rem', border: '1px solid #bbf7d0', fontWeight: 500, padding: '3px 7px' }}>
                                      <i className="bi bi-link-45deg me-1"></i>{p.lnk.trim()}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted fst-italic" style={{ fontSize: '0.72rem' }}>—</span>
                                  )}
                                </td>

                                {/* Status */}
                                <td className="px-3 py-2 text-center">
                                  <Badge style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.65rem', border: '1px solid #86efac', padding: '3px 8px' }}>
                                    <i className="bi bi-check-circle-fill me-1"></i>Aktif
                                  </Badge>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Footer Info */}
                <div className="d-flex justify-content-between align-items-center mt-2.5 px-1 text-muted small" style={{ fontSize: '0.72rem' }}>
                  <span>
                    {selectedGroup ? (
                      <>Detail permisi group <strong>{groups.find(g => g.kode === selectedGroup)?.nama || selectedGroup}</strong>: <strong>{sortedPermissions.length}</strong> fitur (Diurutkan berdasarkan ID Main)</>
                    ) : (
                      <>Menampilkan <strong>{sortedPermissions.length}</strong> dari <strong>{permissions.length}</strong> permisi menu (Diurutkan berdasarkan ID Main)</>
                    )}
                  </span>
                  <span>Terakhir diperbarui: {time.clock}</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.4rem 1rem',
    flexShrink: 0,
  },
  navLinkCustom: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.78rem',
    fontWeight: 500,
    padding: '0.3rem 0.6rem',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  navLinkActive: {
    color: '#ffffff',
    background: 'rgba(255,255,255,0.12)',
  },
  mainContent: {
    flex: 1,
    padding: '0.8rem',
    overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '14px',
    border: '1px solid #cbd5e1',
    boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 0 15px rgba(13, 148, 136, 0.04)',
    background: '#ffffff',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.65rem 1rem',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
  actionBtn: {
    fontSize: '0.73rem',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '0.25rem 0.6rem',
  },
};

export default UserPermission;
